'use client';

import React, { useMemo } from 'react';

import { FundingType } from '@bytel/leviator';
import {
    Alert,
    Divider,
    Icon,
    IconName,
    IconSize,
    RadioTile,
    RadioTiles,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TextMarkup,
    TypographyAlign,
    TypographyBold,
    VariantState,
} from '@bytel/trilogy-react';

import { MobilePlanType, RenewDelayType } from '@app-types/plan';

import { InlinePrice } from '@components/inline-price';

import { PlanSubTypeId } from '@constants/plan';

import { CategoryBox } from '@product-detail/components';
import { revalidateH2Counter } from '@product-detail/helpers';

interface SubTypeSelectorProps {
    planSubType: PlanSubTypeId.CLASSIC | PlanSubTypeId.SMART_CHANGE;
    setPlanSubType: (subTypeId: PlanSubTypeId.CLASSIC | PlanSubTypeId.SMART_CHANGE) => void;
    plans: MobilePlanType[];
    handleOpenSmartChangeModal: () => void;
}

export function SubTypeSelector({
    planSubType,
    setPlanSubType,
    plans,
    handleOpenSmartChangeModal,
}: Readonly<SubTypeSelectorProps>) {
    const handleSubTypeChange = (subType: PlanSubTypeId.CLASSIC | PlanSubTypeId.SMART_CHANGE) => {
        setPlanSubType(subType);
        revalidateH2Counter();
    };

    const planOwned = useMemo(() => plans.find((p) => p.isOwned), [plans]);

    const priceInfo = useMemo(() => {
        const plansBySubType = plans.reduce(
            (acc, plan) => {
                let subType: PlanSubTypeId | 'OTHER';

                switch (plan.subTypeId) {
                    case PlanSubTypeId.SMART_CHANGE:
                        subType = PlanSubTypeId.SMART_CHANGE;
                        break;
                    case PlanSubTypeId.CLASSIC:
                        subType = PlanSubTypeId.CLASSIC;
                        break;
                    default:
                        subType = 'OTHER';
                }

                if (!acc[subType]) {
                    acc[subType] = [];
                }
                acc[subType].push(plan);
                return acc;
            },
            {} as Record<PlanSubTypeId | 'OTHER', MobilePlanType[]>,
        );

        return {
            [PlanSubTypeId.SMART_CHANGE]: getMinimumTodayPrice(plansBySubType[PlanSubTypeId.SMART_CHANGE] ?? []),
            [PlanSubTypeId.CLASSIC]: getMinimumTodayPrice(plansBySubType[PlanSubTypeId.CLASSIC] ?? []),
        };
    }, [plans]);

    const hasMultipleSubTypes = useMemo(
        () => plans.some((plan) => plan.subTypeId !== planSubType),
        [plans, planSubType],
    );

    const classicPlans = useMemo(() => {
        return plans.filter((plan) => plan.subTypeId === PlanSubTypeId.CLASSIC);
    }, [plans]);

    if (!hasMultipleSubTypes) {
        return null;
    }

    return (
        <CategoryBox title='Comment voulez-vous payer votre téléphone&nbsp;?' formId='subTypeChoice'>
            <RadioTiles>
                <RadioTile
                    id={PlanSubTypeId.SMART_CHANGE}
                    horizontal
                    sticker='Nouveau'
                    stickerVariant={VariantState.MAIN}
                    label='Avec Smart Change'
                    description={
                        <>
                            <Text level={TextLevels.TWO} marginless>
                                <Icon name={IconName.CHECK} size={IconSize.SMALL} className='is-marginless' />
                                Changez dès 12 mois si vous voulez
                            </Text>
                            <Text level={TextLevels.TWO} marginless>
                                <Icon name={IconName.CHECK} size={IconSize.SMALL} className='is-marginless' />
                                Etalez votre paiement
                            </Text>
                            <Divider />
                            <Text level={TextLevels.TWO} markup={TextMarkup.SPAN}>
                                À payer aujourd&apos;hui&nbsp;:{' '}
                            </Text>
                            <Text
                                level={TextLevels.TWO}
                                markup={TextMarkup.SPAN}
                                typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                            >
                                {priceInfo[PlanSubTypeId.SMART_CHANGE].hasMultiplePrices && 'à partir de '}
                                <InlinePrice
                                    level={TextLevels.TWO}
                                    amount={priceInfo[PlanSubTypeId.SMART_CHANGE].price}
                                />
                            </Text>
                        </>
                    }
                    name='planSubType'
                    onChange={() => {
                        handleSubTypeChange(PlanSubTypeId.SMART_CHANGE);
                    }}
                    checked={planSubType === PlanSubTypeId.SMART_CHANGE}
                />
                <RadioTile
                    id={PlanSubTypeId.CLASSIC}
                    horizontal
                    label='Sans Smart Change'
                    description={
                        <>
                            <Text level={TextLevels.TWO} marginless>
                                <Icon name={IconName.CHECK} size={IconSize.SMALL} className='is-marginless' />
                                Paiement en {formatPaymentText(classicPlans)}
                            </Text>
                            <Divider />
                            <Text level={TextLevels.TWO} markup={TextMarkup.SPAN}>
                                À payer aujourd&apos;hui&nbsp;:{' '}
                            </Text>
                            <Text
                                level={TextLevels.TWO}
                                markup={TextMarkup.SPAN}
                                typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                            >
                                {priceInfo[PlanSubTypeId.CLASSIC].hasMultiplePrices && 'à partir de '}
                                <InlinePrice level={TextLevels.TWO} amount={priceInfo[PlanSubTypeId.CLASSIC].price} />
                            </Text>
                        </>
                    }
                    name='planSubType'
                    onChange={() => {
                        handleSubTypeChange(PlanSubTypeId.CLASSIC);
                    }}
                    checked={planSubType === PlanSubTypeId.CLASSIC}
                />
            </RadioTiles>
            {planSubType === PlanSubTypeId.CLASSIC && planOwned?.subTypeId === PlanSubTypeId.SMART_CHANGE && (
                <Alert
                    status={VariantState.INFO}
                    title='Vous ne voulez plus profiter de Smart Change ?'
                    description={`En continuant sans Smart Change, vous ne pourrez pas changer votre nouveau téléphone dès ${planOwned?.renewDelay === RenewDelayType.MONTHLY_12 ? 12 : 24} mois.`}
                />
            )}
            <Spacer size={SpacerSize.FIVE} />
            <Text level={TextLevels.TWO} typo={TypographyAlign.TEXT_CENTERED}>
                <button type='button' className='link button-clear' onClick={handleOpenSmartChangeModal}>
                    En savoir plus sur Smart Change
                </button>
            </Text>
        </CategoryBox>
    );
}

interface PriceInfo {
    price: number;
    hasMultiplePrices: boolean;
}

const getMinimumTodayPrice = (plans: MobilePlanType[]): PriceInfo => {
    const initialContributions = plans
        .flatMap(
            (p) =>
                p.simulatedCart?.quotes[0]?.products[0]?.details?.fundings?.map((f) => f.initialContributionWithOdr) ??
                [],
        )
        .filter((contribution): contribution is number => contribution !== undefined);

    const uniqueContributions = [...new Set(initialContributions)];
    const hasMultiplePrices = uniqueContributions.length > 1;

    return {
        price: initialContributions.length ? Math.min(...initialContributions) : Infinity,
        hasMultiplePrices,
    };
};

const formatPaymentText = (plans: MobilePlanType[]): string => {
    const plansWithInstallmentFunding = plans.filter((p) => p.fundings.some((f) => f.type === FundingType.YOUNITED));
    if (plansWithInstallmentFunding.length === 0) {
        return '1 fois';
    }

    const installmentCounts = [
        ...new Set(
            plansWithInstallmentFunding
                .flatMap((p) => p.fundings.filter((f) => f.type === FundingType.YOUNITED))
                .map((f) => f.installments ?? 0),
        ),
    ].sort((a, b) => a - b);

    if (installmentCounts.length === 1) {
        return `1 ou ${installmentCounts[0]} fois`;
    } else {
        const lastCount = installmentCounts.pop();
        return `1, ${installmentCounts.join(', ')} ou ${lastCount} fois`;
    }
};
